import {useEffect, useEffectEvent, useImperativeHandle, useRef} from 'react'

import {useVideo} from '../../../../hooks/use-video'
import type {HeroSequenceProps} from './types'

/**
 * Connects chapter navigation to video playback. Uses the supplied timestamps and loaded video
 * duration to calculate chapter lengths, seek to selected chapters, and report chapter changes.
 *
 * Rejects duplicate, out-of-order, or out-of-bounds timestamps before using them, so chapter
 * lengths are positive and chapter selections stay within the video.
 *
 * Restores position and playback intent after source changes. A selection made while loading
 * takes precedence over the previous position. The component handle accepts commands; changing
 * the displayed chapter alone does not issue a second seek.
 */
export const useHeroSequencePlayback = ({
  ref,
  videoUrl,
  chapterTimestamps,
  currentChapterIndex,
  onNextChapter,
  onReady,
  onActionChange,
  prefersReducedMotion,
}: Omit<HeroSequenceProps, 'chaptersAltDesc' | 'labelledBy' | 'video'> & {
  videoUrl: string | undefined
  chapterTimestamps: NonNullable<HeroSequenceProps['video']>['chapterTimestamps'] | undefined
  onActionChange?: NonNullable<HeroSequenceProps['video']>['onActionChange']
  prefersReducedMotion: boolean
}) => {
  const loadedSourceRef = useRef<string | null>(null)
  const reportedChapterRef = useRef(currentChapterIndex)
  const pendingChapterRef = useRef<number | null>(null)

  const {videoRef, playbackStateSubscription, setPlayback, reportPlayback, handleError, updatePlayback, autoplay} =
    useVideo({
      mediaUrl: videoUrl,
      prefersReducedMotion,
      preservePlaybackIntent: true,
      onActionChange,

      acceptPlayback: media =>
        loadedSourceRef.current !== null &&
        loadedSourceRef.current === videoUrl &&
        media.getAttribute('src') === videoUrl,

      onTimeUpdate: media => {
        const index = chapterTimestamps?.findLastIndex(timestamp => timestamp <= media.currentTime) ?? -1

        if (index >= 0 && index !== reportedChapterRef.current) {
          reportedChapterRef.current = index
          onNextChapter(index)
        }
      },

      onReplay: () => {
        pendingChapterRef.current = loadedSourceRef.current === videoUrl ? null : 0
        reportedChapterRef.current = 0
        onNextChapter(0)
      },

      onError: media => window.reportError(new Error(`Hero video failed to load (media error ${media.error?.code}).`)),
    })

  const initializeMedia = useEffectEvent(() => {
    if (!chapterTimestamps) {
      onReady([])
      return
    }

    const media = videoRef.current
    if (!media) return

    const source = media.getAttribute('src')
    if (media.readyState < media.HAVE_METADATA || source !== videoUrl || loadedSourceRef.current === source) return

    // Chapter lengths come from the gaps between timestamps and the video's end.
    // Require finite, increasing timestamps within the video so every chapter has a positive length.
    if (
      !Number.isFinite(media.duration) ||
      chapterTimestamps.some(
        (timestamp, index) =>
          !Number.isFinite(timestamp) ||
          timestamp < 0 ||
          timestamp >= media.duration ||
          timestamp <= (chapterTimestamps[index - 1] ?? -1),
      )
    ) {
      window.reportError(
        new Error('Hero video chapter timestamps must be in chronological order and within the video duration.'),
      )
      return
    }

    const previous = playbackStateSubscription.getState()
    const chapterIndex = pendingChapterRef.current ?? currentChapterIndex
    const chapterStart = chapterTimestamps[chapterIndex]

    if (chapterStart === undefined) {
      window.reportError(new Error(`Hero video has no timestamp for chapter ${chapterIndex}.`))
      return
    }

    // A selection made during loading takes precedence over restoring the previous position.
    const position =
      pendingChapterRef.current === null && previous
        ? previous.isFinished
          ? media.duration
          : previous.currentTime
        : chapterStart

    loadedSourceRef.current = source
    reportedChapterRef.current = chapterIndex
    pendingChapterRef.current = null
    media.currentTime = Math.min(position, media.duration)

    // Readiness precedes the restored-position notification, as it did with native playback.
    onReady(
      chapterTimestamps.map((timestamp, index) => (chapterTimestamps[index + 1] ?? media.duration) - timestamp),
      playbackStateSubscription,
    )
    updatePlayback(media, 'loadedmetadata')

    if (previous && !previous.isPaused && !previous.isFinished) {
      void setPlayback('play', 'automatic')
    } else {
      autoplay()
    }
  })

  useEffect(() => {
    const media = videoRef.current
    if (loadedSourceRef.current !== videoUrl) loadedSourceRef.current = null

    const handleMetadata = () => initializeMedia()
    media?.addEventListener('loadedmetadata', handleMetadata)

    // Metadata may already have loaded before hydration; no-video sequences are ready immediately.
    handleMetadata()

    return () => media?.removeEventListener('loadedmetadata', handleMetadata)
  }, [videoUrl, videoRef])

  useImperativeHandle(ref, () => ({
    setPlayback,

    seekToChapter: index => {
      const timestamp = chapterTimestamps?.[index]

      if (chapterTimestamps && timestamp === undefined) {
        window.reportError(new Error(`Hero video has no timestamp for chapter ${index}.`))
        return
      }

      // Keep the latest selection for initialization if this source is not ready yet.
      pendingChapterRef.current = index

      const media = videoRef.current
      if (!media || timestamp === undefined || loadedSourceRef.current !== videoUrl) return

      pendingChapterRef.current = null
      reportedChapterRef.current = index
      media.currentTime = timestamp
    },
  }))

  return {videoRef, reportPlayback, handleError}
}
